<template>
  <div>
    <div class="image-container">
      <Draggable
        v-model="imgList"
        :animation="200"
        item-key="index"
        class="draggable"
        @end="dragEnd"
      >
        <template #item="{element, index}">
          <div class="image-wrap">
            <!-- v-for="(item, index) in imgList" :key="index" -->
            <div class="image-progress" :style="imageStyle">
              <el-image
                v-if="element[imgUrlKey]"
                :src="`${element[imgUrlKey]}?x-oss-process=image/resize,w_118,m_lfit`"
                :preview-src-list="getPreviewImgList(index)"
                class="image-item"
                :style="imageStyle"
                fit="cover"
              >
                <template #error />
              </el-image>

              <div
                v-if="element.processLen && element.processLen < 100"
                class="progress-wrap"
                :style="imageStyle"
              >
                <div class="progress" :style="{ width: imageStyle.width }">
                  {{ element.processLen }}%
                </div>
              </div>
            </div>

            <el-icon size="18" color="#c4c4c4" class="iconicon_sousuo3x1" @click="deleteImage(index)"><CircleCloseFilled /></el-icon>
            <!-- <i
              class="iconfont iconicon_sousuo3x1"
              @click="deleteImage(index)"
            /> -->

            <div class="icon-bg" />

            <el-popover
              v-if="isEditImg"
              ref="popoverRefs"
              placement="bottom"
              width="400"
              :offset="180"
              trigger="manual"
              popper-class="edit-popover"
            >
              <div class="content">
                <el-input
                  v-model="editInputValue"
                  class="edit-input"
                  :maxlength="maxlength"
                />
                <div class="btn-wrap">
                  <el-button text class="save-btn" @click="saveImgName(element)">
                    保存
                  </el-button>
                  <el-button text class="cancel-btn" @click="closeEditPopover">
                    取消
                  </el-button>
                </div>
              </div>

              <template #reference>
                <div
                  class="image-desc"
                  @click="openEditPopover(element[imgNameKey], index)"
                >
                  <span v-if="!element[imgNameKey] && imgNameRequired" class="star">*</span>
                  <span v-if="!element[imgNameKey]">{{ editTips }}</span>
                  <span
                    v-else
                    class="desc-text"
                    :style="{ width: imageStyle.width }"
                  >
                    {{ element[imgNameKey] }}
                  </span>
                </div>
              </template>
            </el-popover>
          </div>
        </template>
      </Draggable>

      <el-upload
        ref="uploadImageRef"
        v-if="imgList.length < limit"
        action="#"
        list-type="picture-card"
        drag
        class="upload-image"
        :style="imageStyle"
        :http-request="uploadImage"
        :show-file-list="false"
        :multiple="limit > 1"
        :limit="limit === 1 ? limit : 1000"
        :on-change="fileChange"
      >
        <div class="text-desc">
          <el-icon size="24" color="#8c939d"><Plus /></el-icon>
          <div>{{ uploadText }}</div>
        </div>
      </el-upload>
    </div>

    <div class="upload-desc">
      <slot name="uploadDesc">{{ uploadDesc }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
// import dayjs from 'dayjs'
import md5 from 'js-md5'
import Draggable from 'vuedraggable'
// import {
//   ElImage,
//   ElUpload,
//   ElPopover,
//   ElInput,
//   ElButton,
//   ElMessage,
//   ElIcon
// } from 'element-plus'
import { Plus, CircleCloseFilled } from '@element-plus/icons-vue'

/* ---------------- props ---------------- */
interface ImageItem {
  [key: string]: any
  uid?: string
  processLen?: number
}

const props = defineProps({
  imageList: { type: Array as () => ImageItem[], default: () => [] },
  imgUrlKey: { type: String, default: 'imgUrl' },
  imgNameKey: { type: String, default: 'imgName' },
  ossClient: { type: Object as () => any, required: true },
  limit: { type: Number, default: 1 },
  maxSize: { type: Number, default: 5 * 1024 * 1024 },
  errorText: { type: String, default: '上传失败，文件应为不超过5MB的JPG、PNG格式。' },
  uploadDesc: { type: String, default: '最多上传10张，格式:JPG/PNG' },
  isEditImg: { type: Boolean, default: false },
  editTips: { type: String, default: '添加客户名称' },
  uploadText: { type: String, default: '上传图片' },
  maxlength: { type: Number, default: 10 },
  imgNameRequired: { type: Boolean, default: true },
  uploadImagePath: { type: String, default: '' },
  imageStyle: {
    type: Object,
    default: () => ({ width: '120px', height: '120px' })
  }
})

const emit = defineEmits(['update:imageList', 'afterUpload'])

/* ---------------- state ---------------- */
const imgList = ref<ImageItem[]>([])
const editInputValue = ref('')
const uploadImageRef = ref<any>()
const popoverRefs = ref<any[]>([])

/* ---------------- watch ---------------- */
watch(
  () => props.imageList,
  (val) => {
    imgList.value = val ? [...val].map((item, index) => ({ ...item, index })) : []
  },
  { immediate: true }
)

/* ---------------- methods ---------------- */
const getPreviewImgList = (index: number) => {
  const result: string[] = []
  for (let i = 0; i < imgList.value.length; i++) {
    result.push(imgList.value[(index + i) % imgList.value.length][props.imgUrlKey])
  }
  return result
}

const dragEnd = () => {
  emit('update:imageList', [...imgList.value])
  emit('afterUpload', [...imgList.value])
}

const uploadImage = async (data: any) => {
  const file = data.file
  if (!/image\/(png|jpg|jpeg)/.test(file.type) || file.size > props.maxSize) {
    ElMessage.error(props.errorText)
    return
  }

  const url = await getUploadOssImageUrl(data)
  const target = imgList.value.find((i) => i.uid === file.uid)
  const index = imgList.value.findIndex(i => i.uid === file.uid)
  imgList.value[index].processLen = 100
  if (target) {
    target[props.imgUrlKey] = url
    delete target.uid
    delete target.processLen
  }

  emit('update:imageList', [...imgList.value])
  emit('afterUpload', [...imgList.value])
}

const getUploadOssImageUrl = async (data: any) => {
  // const date = dayjs().format('YYYYMMDD')
  const imgType = data.file.type.split('/').pop()
  const name = md5(String(Date.parse(new Date()))) + Math.round(Math.random() * 100000) + "." + imgType

  const result = await props.ossClient.put(
    `${props.uploadImagePath}/${name}`,
    data.file
  )
  return result.url.replace(
    'http://beautydata-assistant.oss-cn-hangzhou.aliyuncs.com',
    'https://workres.meiyeyanjiu.com'
  )
  // return result.url.replace(
  //   'http://zhaoyl-et-file.oss-cn-hangzhou.aliyuncs.com',
  //   'https://resoss.meiyeyanjiu.com'
  // )
}

const deleteImage = (index: number) => {
  imgList.value.splice(index, 1)
  emit('update:imageList', [...imgList.value])
  emit('afterUpload', [...imgList.value])
}

const openEditPopover = async (name: string, index: number) => {
  closeEditPopover()
  editInputValue.value = name
  await nextTick()
  popoverRefs.value[index]?.doShow()
}

const closeEditPopover = () => {
  popoverRefs.value.forEach((p) => p?.doClose())
}

const saveImgName = (item: ImageItem) => {
  item[props.imgNameKey] = editInputValue.value
  closeEditPopover()
  emit('update:imageList', [...imgList.value])
  emit('afterUpload', [...imgList.value])
}

const fileChange = (file: any) => {
  if (!/\.(jpg|jpeg|png)$/i.test(file.name) || file.size > props.maxSize) {
    uploadImageRef.value?.clearFiles()
    return
  }
  
  if (file.status === 'ready' && imgList.value.length < props.limit) {
    let obj: ImageItem = { uid: file.uid, processLen: 0, index: imgList.value.length }
    if (props.isEditImg) obj[props.imgNameKey] = ''
    imgList.value.push(obj)
    // 等待进度条显示完成再添加到imgList
    const timer = setInterval(() => {
      const currentItem = imgList.value.find(item => item.uid === file.uid)
      if (!currentItem || currentItem.processLen! >= 90) {
        clearInterval(timer)
        return
      }
      currentItem.processLen! += Math.floor(Math.random() * 10 + 1)
    }, 80)
    
  }
}
</script>

<style lang="scss" scoped>
:deep(.image-container) {
  display: flex;
  max-width: 700px;
  flex-wrap: wrap;
  line-height: initial;
  .draggable {
    display: contents;
  }
  .image-wrap {
    position: relative;
    display: inline-block;
    margin-bottom: 10px;
    margin-right: 16px;
    .el-popover {
      z-index: 100 !important;
    }
    .image-progress {
      position: relative;
      .progress-wrap {
        // width: 120px;
        // height: 120px;
        border: 1px solid #efefef;
        position: absolute;
        top: 0;
        background: #fff;
        z-index: 2;
        box-sizing: border-box;

        .progress {
          // width: 120px;
          height: 24px;
          background: rgba(0, 0, 0, 0.5);
          color: #fff;
          position: absolute;
          bottom: 0;
          left: -1px;
          font-size: 14px;
          line-height: 24px;
          text-align: center;
        }
      }
    }
    .image-item {
      // width: 120px;
      // height: 120px;
      border: 1px solid transparent;
      box-sizing: border-box;
    }
    .iconicon_sousuo3x1 {
      position: absolute;
      color: #c4c4c4;
      font-size: 18px;
      top: -8px;
      right: -8px;
      z-index: 2;
      cursor: pointer;
      display: none;
      &.icon-disabled {
        pointer-events: none;
      }
    }
    .icon-bg {
      width: 16px;
      height: 16px;
      background: #fff;
      border-radius: 50%;
      position: absolute;
      top: -7px;
      right: -7px;
      display: none;
    }
    .image-desc {
      font-size: 12px;
      position: absolute;
      background: #999999;
      bottom: 4px;
      left: 1px;
      width: 118px;
      height: 24px;
      line-height: 24px;
      text-align: center;
      color: #ffffff;
      cursor: pointer;
      .star {
        color: #ff0000;
      }
      .desc-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
        width: 120px;
        padding: 0 3px;
      }
    }
    &:hover {
      .image-item {
        border: 1px solid #4d70f8;
      }
      .iconicon_sousuo3x1,
      .icon-bg {
        display: block;
      }
    }
  }
  .el-upload:focus {
    color: #ff4080;
  }
  .upload-image {
    position: relative;
    margin-right: 16px;
    .el-upload--picture-card {
      border: none;
      background-color: #f7f8fa;
      border-radius: 4px;
      width: inherit;
      height: inherit;
      .el-upload-dragger {
        background: initial;
        border: none;
        border-radius: 0;
        width: initial;
        height: initial;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      &:hover {
        color: #ff4080;
      }
    }
    .text-desc {
      line-height: 24px;
      font-size: 12px;
      .el-icon-plus {
        font-size: 24px;
      }
    }

    &.upload-disabled {
      .el-upload--picture-card {
        cursor: not-allowed;
      }
    }
  }
}

.upload-desc {
  font-size: 12px;
  color: #999999;
}

.edit-popover {
  .content {
    display: flex;
    .edit-input {
      width: 277px;
    }

    .btn-wrap {
      width: 92px;
      .save-btn {
        color: #4d70f8;
        margin-left: 10px;
        position: relative;
        &::after {
          content: "";
          position: absolute;
          width: 1px;
          height: 12px;
          background: #e5e5e5;
          right: -10px;
          top: 13px;
        }
      }
      .cancel-btn {
        color: #333333;
        margin-left: 17px;
      }
    }
  }
}

:deep(.image-slot) {
  .myyjy-loading-wrap {
    width: 118px;
  }
}
</style>
